import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import type {
  TriggerListItem,
  TriggerDetailView,
  TriggerExecution,
  ResolvedWatch,
  WatchedEntity,
} from "./types";
import type { AppRuntime } from "../../runtime/contracts/runtime";

interface UseTriggersResult {
  readonly triggers: readonly TriggerListItem[];
  readonly loading: boolean;
  readonly deleteTrigger: (id: string) => Promise<void>;
  readonly setStatus: (id: string, status: string) => Promise<void>;
  readonly reload: () => Promise<void>;
}

/** Fetch triggers watching ANY of the given entity IDs, deduplicated. */
export function useTriggersForEntities(entityIds: readonly string[]): UseTriggersResult {
  const runtime = useAppRuntime();
  const [triggers, setTriggers] = useState<TriggerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const prevKey = useRef<string>("");

  const load = useCallback(async () => {
    if (entityIds.length === 0) {
      setTriggers([]);
      setLoading(false);
      return;
    }
    try {
      // Query triggers for each entity, deduplicate by trigger id
      const allResults = await Promise.all(
        entityIds.map((id) =>
          runtime.transport
            .rpc<TriggerListItem[]>("triggers.list_for_entity", { entity_id: id })
            .then((r) => (Array.isArray(r) ? r : []))
            .catch(() => [] as TriggerListItem[]),
        ),
      );
      const seen = new Set<string>();
      const deduped: TriggerListItem[] = [];
      for (const batch of allResults) {
        for (const t of batch) {
          if (!seen.has(t.id)) {
            seen.add(t.id);
            deduped.push(t);
          }
        }
      }
      setTriggers(deduped);
    } catch {
      setTriggers([]);
    } finally {
      setLoading(false);
    }
  }, [entityIds, runtime]);

  useEffect(() => {
    const key = [...entityIds].sort().join(",");
    if (key !== prevKey.current) {
      prevKey.current = key;
      setLoading(true);
      void load();
    }
  }, [entityIds, load]);

  const deleteTrigger = useCallback(
    async (id: string): Promise<void> => {
      await runtime.transport.rpc("triggers.delete", { id });
      setTriggers((prev) => prev.filter((t) => t.id !== id));
    },
    [runtime],
  );

  const setStatus = useCallback(
    async (id: string, status: string): Promise<void> => {
      const updated = await runtime.transport.rpc<TriggerListItem>("triggers.update", {
        id,
        status,
      });
      setTriggers((prev) => prev.map((t) => (t.id === id ? updated : t)));
    },
    [runtime],
  );

  return { triggers, loading, deleteTrigger, setStatus, reload: load };
}

/** Pure async logic for resolving watched entities — testable without React. */
export async function resolveWatches(
  watchedEntities: readonly WatchedEntity[],
  rpc: <T>(method: string, params: Record<string, unknown>) => Promise<T>,
): Promise<ResolvedWatch[]> {
  return Promise.all(
    watchedEntities.map((w) =>
      rpc<Record<string, unknown>>("graph.entity.get", { id: w.id })
        .then((e): ResolvedWatch => ({
          id: w.id,
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          schema_id: (e.schema_id as string) ?? "",
          data: e,
        }))
        .catch((): ResolvedWatch => ({
          id: w.id,
          schema_id: "",
          data: { id: w.id, name: w.name ?? w.id },
          error: true,
        })),
    ),
  );
}

export function useResolvedWatches(
  watchedEntities: readonly WatchedEntity[] | undefined,
  runtime: AppRuntime,
): readonly ResolvedWatch[] {
  const [resolved, setResolved] = useState<ResolvedWatch[]>([]);

  useEffect(() => {
    if (!watchedEntities || watchedEntities.length === 0) {
      setResolved([]);
      return;
    }
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    resolveWatches(watchedEntities, runtime.transport.rpc.bind(runtime.transport))
      .then((results) => {
        if (!cancelled) setResolved(results);
      });
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { cancelled = true; };
  }, [watchedEntities, runtime]);

  return resolved;
}

export function useTriggerDetail(triggerId: string | undefined): {
  trigger: TriggerDetailView | null;
  loading: boolean;
  history: readonly TriggerExecution[];
  loadHistory: () => Promise<void>;
} {
  const runtime = useAppRuntime();
  const [trigger, setTrigger] = useState<TriggerDetailView | null>(null);
  const [history, setHistory] = useState<TriggerExecution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!triggerId) {
      setTrigger(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void runtime.transport
      .rpc<TriggerDetailView>("triggers.get", { id: triggerId })
      .then(setTrigger)
      .finally(() => { setLoading(false); });
  }, [triggerId, runtime]);

  const loadHistory = useCallback(async () => {
    if (!triggerId) return;
    const result = await runtime.transport.rpc<TriggerExecution[]>(
      "triggers.fire_history",
      { trigger_id: triggerId, limit: 20 },
    );
    setHistory(Array.isArray(result) ? result : []);
  }, [triggerId, runtime]);

  return { trigger, loading, history, loadHistory };
}
